import React from 'react';
import { Box, Grid2 } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { useGetFacilityClinicalMappingDetailsForView } from '../../API/hooks/FacilityClinicianMappingHook';
import { hasRole, getUserFromStorage } from '../login/storageUtility';
import CustomTypography from '@qler-view/shared_lib/src/shared/lib/ui-components/Typography/CustomTypography';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import CustomDivider from '@qler-view/shared_lib/src/shared/lib/ui-components/Divider/CustomDivider';
import {
  CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH,
  FACILITY_CLINICAIN_MAPPED_LIST_ROUTE_PATH,
  NEW_CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH,
  NEW_FACILITY_CLINICIAN_MAPPED_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomIconButton from '@qler-view/shared_lib/src/shared/lib/ui-components/IconButton/CustomIconButton';
import BackIcon from '@assets/images/BackIcon.svg';

type facilityDeatils = {
  facilityName?: string;
  providerName?: string;
  action: string;
  type: string;
  ids?: number;
};

const getValueOrNA = (value: string | undefined | null) =>
  value && value.trim() !== '' ? value : 'N/A';

const FacilityClinicianMappingView: React.FC = () => {
  const navigate = useNavigate();
  const user = getUserFromStorage();
  const location = useLocation() as {
    state?: facilityDeatils;
  };
  const { type, ids } = location.state || {};
  const { FacilityClinicainMappingDetailsForView, isLoading } =
    useGetFacilityClinicalMappingDetailsForView(
      user?.userId.toString() ?? '',
      Number(ids)
    );

  const handelMappingEdit = (id: number) => {
    if (type === 'facility') {
      navigate(
        NEW_FACILITY_CLINICIAN_MAPPED_ROUTE_PATH +
          `/${FacilityClinicainMappingDetailsForView?.facilityId}`,
        {
          state: {
            facilityName: FacilityClinicainMappingDetailsForView?.facilityName,
            action: 'edit',
            type: type,
            ids: id,
          },
        }
      );
      return;
    }
    navigate(
      NEW_CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH +
        `/${FacilityClinicainMappingDetailsForView?.providerId}`,
      {
        state: {
          facilityName: FacilityClinicainMappingDetailsForView?.providerName,
          action: 'edit',
          type: type,
          ids: id,
        },
      }
    );
  };

  const getDisplayName = () => {
    if (type === 'clinician') {
      return `View Clinician Facility Mapping - (${FacilityClinicainMappingDetailsForView?.providerName})`;
    }
    return `View Facility Clinician Mapping - (${FacilityClinicainMappingDetailsForView?.facilityName})`;
  };

  const handleBackNavigation = () => {
    if (type === 'facility') {
      navigate(
        FACILITY_CLINICAIN_MAPPED_LIST_ROUTE_PATH +
          `/${FacilityClinicainMappingDetailsForView?.facilityId}`,
        {
          state: {
            facilityName: FacilityClinicainMappingDetailsForView?.facilityName,
            type: type,
            ids: FacilityClinicainMappingDetailsForView?.facilityId,
          },
        }
      );
    } else {
      navigate(
        CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH +
          `/${FacilityClinicainMappingDetailsForView?.providerId}`,
        {
          state: {
            providerName: FacilityClinicainMappingDetailsForView?.providerName,
            type: type,
            ids: FacilityClinicainMappingDetailsForView?.facilityId,
          },
        }
      );
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="flex-justify-space-between align-items-baseline">
              <Box className="text-align-left display-flex">
                <CustomIconButton
                  id="back-button"
                  icon={<BackIcon />}
                  onClick={handleBackNavigation}
                ></CustomIconButton>
                <CustomTypography
                  variant="h4"
                  className="pl-8"
                  id="add-new-facility-text"
                >
                  {getDisplayName()}
                </CustomTypography>
              </Box>
              {hasRole(['ops-admin']) ? (
                <Box>
                  <CustomButton
                    id="save-button"
                    onClick={() => {
                      handelMappingEdit(
                        FacilityClinicainMappingDetailsForView?.id || 0
                      );
                    }}
                    variant="contained"
                  >
                    Edit
                  </CustomButton>
                </Box>
              ) : null}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="form-background p-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-32"
                    id="provider-facility-info-text"
                  >
                    Provider Facility Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex">
                <Grid2 container spacing={4} className="width-80">
                  {type === 'clinician' && (
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-name-label"
                        variant="body3"
                        color="textDisabled"
                        className="text-align-left pb-12"
                      >
                        Facility Name
                      </CustomTypography>
                      <CustomTypography
                        id="facility-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left"
                      >
                        {getValueOrNA(
                          FacilityClinicainMappingDetailsForView?.facilityName
                        )}
                      </CustomTypography>
                    </Grid2>
                  )}
                  {type === 'facility' && (
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="provider-name-label"
                        variant="body3"
                        color="textDisabled"
                        className="text-align-left pb-12"
                      >
                        Provider Name
                      </CustomTypography>
                      <CustomTypography
                        id="provider-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left"
                      >
                        {getValueOrNA(
                          FacilityClinicainMappingDetailsForView?.providerName
                        )}
                      </CustomTypography>
                    </Grid2>
                  )}
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-assigned-email-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Facility Assigned Email
                    </CustomTypography>
                    <CustomTypography
                      id="facility-assigned-email-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.facilityAssignedEmail
                      )}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="primary-phone-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Primary Phone
                    </CustomTypography>
                    <CustomTypography
                      id="primary-phone-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.primaryPhone
                      )}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="username-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Username
                    </CustomTypography>
                    <CustomTypography
                      id="username-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.username
                      )}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="network-access-id-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Network Access ID
                    </CustomTypography>
                    <CustomTypography
                      id="network-access-id-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.networkAccessId
                      )}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="emr-id-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      EMR ID
                    </CustomTypography>
                    <CustomTypography
                      id="emr-id-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.emrId
                      )}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="vpn-gateway-id-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      VPN Gateway ID
                    </CustomTypography>
                    <CustomTypography
                      id="vpn-gateway-id-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.vpnGatewayId
                      )}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="privileges-issued-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Privileges Issued
                    </CustomTypography>
                    <CustomTypography
                      id="privileges-issued-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.privilegesIssuedDate
                      )}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="mfa-configuration-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      MFA Configuration
                    </CustomTypography>
                    <CustomTypography
                      id="mfa-configuration-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.mfaConfiguration
                      )}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="mfa-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      MFA
                    </CustomTypography>
                    <CustomTypography
                      id="mfa-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left "
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.mfa
                      )}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="status-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Status
                    </CustomTypography>
                    <Box className="text-align-left">
                      {FacilityClinicainMappingDetailsForView?.status ? (
                        <Box className="display-flex align-items-baseline">
                          <Box component="span" className="status-active-dot" />
                          <CustomTypography
                            id="status-active-value"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pl-4"
                          >
                            {' '}
                            Active
                          </CustomTypography>
                        </Box>
                      ) : (
                        <Box className="display-flex align-items-baseline">
                          <Box
                            component="span"
                            className="status-inactive-dot"
                          />
                          <CustomTypography
                            id="status-inactive-value"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pl-4"
                          >
                            {' '}
                            Inactive
                          </CustomTypography>
                        </Box>
                      )}
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="provider-setup-title"
                    >
                      Provider Setup
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="follow-up-contact-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Follow-Up Contact
                    </CustomTypography>
                    <CustomTypography
                      id="follow-up-contact-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.followUpContact
                      )}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="training-progress-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Training Progress
                    </CustomTypography>

                    <CustomTypography
                      id="training-progress-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.trainingProgress
                      )}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="training-type-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Training Type
                    </CustomTypography>

                    <CustomTypography
                      id="training-type-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {getValueOrNA(
                        FacilityClinicainMappingDetailsForView?.trainingType
                      )}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="follow-up-required-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Follow-Up Required
                    </CustomTypography>
                    <Box className="text-align-left">
                      {FacilityClinicainMappingDetailsForView?.followUpRequired ? (
                        <Box className="display-flex align-items-baseline">
                          <Box component="span" className="status-active-dot" />
                          <CustomTypography
                            id="follow-up-required-value"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pl-4"
                          >
                            {' '}
                            Yes
                          </CustomTypography>
                        </Box>
                      ) : (
                        <Box className="display-flex align-items-baseline">
                          <Box
                            component="span"
                            className="status-inactive-dot"
                          />
                          <CustomTypography
                            id="follow-up-required-value"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pl-4"
                          >
                            {' '}
                            No
                          </CustomTypography>
                        </Box>
                      )}
                    </Box>
                  </Grid2>
                </Grid2>

                <Box className="pt-32">
                  <CustomDivider />
                </Box>
                <Box className="pt-32">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="provider-facility-notes-text"
                      >
                        Provider Facility Notes
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="provider-facility-notes-label"
                        variant="body3"
                        color="textDisabled"
                        className="text-align-left pb-12"
                      >
                        Notes
                      </CustomTypography>
                      <CustomTypography
                        id="provider-facility-notes-value"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left"
                      >
                        {getValueOrNA(
                          FacilityClinicainMappingDetailsForView?.providerFacilityNotes
                        )}
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default FacilityClinicianMappingView;
